/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { useTranslate } from "@adapptio/lib-components";
import {
	isSchemaConstArrayModel,
	isSchemaConstMapModel,
	isSchemaConstMapModelEntry,
	isSchemaConstObjectModel,
	isSchemaConstStringModel,
	TGenericModelNode
} from "@adapptio/lib-blueprint";
import {
	isSelectionItemBlock,
	isSelectionItemStyle,
	TThemeEditSelectionItem
} from "../../../Common/ThemeEditSelection";
import { InspectMapEntry } from "../../../../../ui/components/Inspector/InspectMapEntry";
import { InspectArrayItem } from "../../../../../ui/components/Inspector/InspectArrayItem";

export interface IThemeEditorInspectorItemProps {
	selectionItem: TThemeEditSelectionItem;
	getNodeById: (nodeId: number) => TGenericModelNode;
}

const pathBlock = [ "$block" ];
const pathStyle = [ "$style" ];

export const ThemeEditorInspectorItem: React.FC<IThemeEditorInspectorItemProps> = ({
	selectionItem,
	getNodeById
}) => {
	const t = useTranslate();

	if (isSelectionItemBlock(selectionItem)) {
		const modelNode = getNodeById(selectionItem.nodeId);
		const parentListModel = getNodeById(selectionItem.parentListModelId);

		if (!isSchemaConstObjectModel(modelNode) || !isSchemaConstArrayModel(parentListModel)) {
			return null;
		}

		const nameModel = isSchemaConstStringModel(modelNode.props.name) ? modelNode.props.name : undefined;

		return (
			<InspectArrayItem
				model={modelNode}
				parentListModel={parentListModel}
				nameModel={nameModel}
				depth={0}
				path={pathBlock}
				title={t("editor", "Block")}
			/>
		);
	}

	if (isSelectionItemStyle(selectionItem)) {
		const parentModel = getNodeById(selectionItem.parentMapModelId);
		const modelEntry = selectionItem.mapEntry;

		if (!isSchemaConstMapModel(parentModel) || !isSchemaConstMapModelEntry(modelEntry)) {
			return null;
		}

		return (
			<InspectMapEntry
				model={parentModel}
				modelEntry={modelEntry}
				path={pathStyle}
				depth={0}
				title={t("editor", "Style")}
			/>
		);
	}

	return null;
};
